import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@lib/supabase/client';

// Generate or retrieve a unique session ID for anonymous visitors
const getOrCreateSessionId = (): string => {
  const storageKey = 'visitor_session_id';
  let sessionId = sessionStorage.getItem(storageKey);
  
  if (!sessionId) {
    // Generate a unique session ID
    sessionId = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    sessionStorage.setItem(storageKey, sessionId);
  }
  
  return sessionId;
};

export const usePageVisitTracker = () => {
  const location = useLocation();
  const lastTrackedPath = useRef<string | null>(null);
  const isTrackingRef = useRef(false);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 2;

  useEffect(() => {
    const trackPageVisit = async () => {
      // Prevent duplicate tracking for the same path
      if (lastTrackedPath.current === location.pathname || isTrackingRef.current) {
        return;
      }

      isTrackingRef.current = true;
      retryCountRef.current = 0;

      const attemptInsert = async (): Promise<boolean> => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const sessionId = getOrCreateSessionId();

          const { error } = await supabase.from('page_visits').insert({
            user_id: session?.user?.id || null,
            session_id: sessionId,
            page_path: location.pathname,
            user_agent: navigator.userAgent,
          });

          if (error) {
            console.error('Error inserting page visit:', error);
            return false;
          }

          return true;
        } catch (error) {
          console.error('Error tracking page visit:', error);
          return false;
        }
      };

      let success = await attemptInsert();
      
      // Retry on failure
      while (!success && retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        await new Promise(resolve => setTimeout(resolve, 1000 * retryCountRef.current));
        success = await attemptInsert();
      }

      if (success) {
        lastTrackedPath.current = location.pathname;
      }

      isTrackingRef.current = false;
    };

    trackPageVisit();
  }, [location.pathname]);
};
